import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, PlayCircle, Trophy } from "lucide-react";
import { api } from "@/lib/handball";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * NewMatchPage — top-level "Ny match" flow:
 *   1. Välj deltagande målvakter (checkboxar)
 *   2. Välj typ (match/träning)
 *   3. Match-detaljer (namn, motståndare, turnering)
 *   4. Skapa → navigera direkt till logging-vyn för första MV i listan.
 */
export default function NewMatchPage() {
  const navigate = useNavigate();
  const [goalkeepers, setGoalkeepers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedGks, setSelectedGks] = useState([]);
  const [primaryGk, setPrimaryGk] = useState(null);
  const [sessType, setSessType] = useState("match");
  const [name, setName] = useState("");
  const [opponent, setOpponent] = useState("");
  const [tournamentId, setTournamentId] = useState("__none__");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [gks, torn] = await Promise.all([
          api.get("/goalkeepers"),
          api.get("/tournaments", {
            params: {
              scope: "all",
              active_on: new Date().toISOString().slice(0, 10),
            },
          }),
        ]);
        // Writers can log matches; viewers cannot start a session.
        const canWrite = gks.data.filter((g) => g.role !== "viewer");
        setGoalkeepers(canWrite);
        setTournaments(torn.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleGk = (id) => {
    setSelectedGks((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      // Keep primary consistent
      if (!next.includes(primaryGk)) {
        setPrimaryGk(next[0] || null);
      } else if (!primaryGk && next.length > 0) {
        setPrimaryGk(next[0]);
      }
      return next;
    });
  };

  const canSubmit = useMemo(
    () => selectedGks.length > 0 && name.trim().length > 0 && primaryGk,
    [selectedGks, name, primaryGk]
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        session_type: sessType,
        opponent: sessType === "match" && opponent.trim() ? opponent.trim() : null,
        goalkeeper_id: primaryGk,
        tournament_id: tournamentId === "__none__" ? null : tournamentId,
        participating_goalkeeper_ids:
          selectedGks.length > 1 ? selectedGks : null,
      };
      const { data } = await api.post("/sessions", payload);
      toast.success(`Session ${data.name} skapad`);
      navigate(`/gk/${primaryGk}/session/${data.id}/register`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte skapa match");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen">
      <AppHeader subtitle="Ny match" backTo="/" backLabel="Startsida" />
      <main className="max-w-[900px] mx-auto px-6 py-10 space-y-8">
        <div className="flex items-center gap-3 text-zinc-400">
          <button
            type="button"
            data-testid="new-match-back"
            onClick={() => navigate("/")}
            className="flex items-center gap-1 hover:text-white"
          >
            <ChevronLeft size={16} /> Avbryt
          </button>
        </div>

        {loading ? (
          <div className="text-zinc-500 font-display uppercase tracking-widest">
            Laddar…
          </div>
        ) : goalkeepers.length === 0 ? (
          <div className="card-tactical p-10 text-center">
            <div className="font-display uppercase tracking-widest text-xl text-white mb-2">
              Inga målvakter att logga på
            </div>
            <div className="text-zinc-400 mb-6">
              Du behöver skapa en profil eller få en delad med dig först.
            </div>
            <Button
              data-testid="new-match-goto-profiles"
              onClick={() => navigate("/profiles")}
              className="rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest"
            >
              Till profiler
            </Button>
          </div>
        ) : (
          <>
            {/* Step 1: participating goalkeepers */}
            <section className="card-tactical p-6">
              <div className="section-hdr mb-4">
                <span className="marker" />
                Målvakter som spelar
              </div>
              <div className="text-sm text-zinc-400 mb-4">
                Markera alla som är med. Du kan växla fritt mellan dem under matchen.
                Första i listan blir startmålvakt.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {goalkeepers.map((g) => {
                  const checked = selectedGks.includes(g.id);
                  const isPrimary = primaryGk === g.id;
                  return (
                    <label
                      key={g.id}
                      data-testid={`newmatch-gk-${g.id}`}
                      className={`flex items-center gap-3 p-3 border transition-colors cursor-pointer ${
                        checked
                          ? "border-[#34C759] bg-[#34C759]/5"
                          : "border-[#27272A] hover:border-[#3F3F46]"
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleGk(g.id)}
                        className="border-[#3F3F46] data-[state=checked]:bg-[#34C759] data-[state=checked]:text-black rounded-none"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-display uppercase tracking-widest text-white text-sm truncate">
                          {g.name}
                        </div>
                        {g.role && g.role !== "owner" && (
                          <div className="text-[10px] text-zinc-500 mt-0.5 truncate">
                            Delad av {g.owner_name}
                          </div>
                        )}
                      </div>
                      {isPrimary && checked && (
                        <span className="text-[9px] font-bold tracking-widest border border-[#34C759] text-[#34C759] px-1.5 py-0.5">
                          START
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </section>

            {/* Step 2: type */}
            <section className="card-tactical p-6">
              <div className="section-hdr mb-4">
                <span className="marker" />
                Typ
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="newmatch-type-match"
                  data-active={sessType === "match"}
                  onClick={() => setSessType("match")}
                  className="seg-btn h-14"
                >
                  Match
                </button>
                <button
                  type="button"
                  data-testid="newmatch-type-training"
                  data-active={sessType === "traning"}
                  onClick={() => setSessType("traning")}
                  className="seg-btn h-14"
                >
                  Träning
                </button>
              </div>
            </section>

            {/* Step 3: details */}
            <section className="card-tactical p-6 space-y-4">
              <div className="section-hdr mb-2">
                <span className="marker" />
                Detaljer
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase tracking-widest text-xs">
                  Namn
                </Label>
                <Input
                  data-testid="newmatch-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={sessType === "match" ? "T.ex. Vårserien omg 3" : "T.ex. Måndagsträning"}
                  className="bg-black border-[#3F3F46] text-white focus-visible:ring-[#34C759] rounded-none"
                />
              </div>
              {sessType === "match" && (
                <div className="space-y-2">
                  <Label className="text-zinc-400 uppercase tracking-widest text-xs">
                    Motståndare (valfritt)
                  </Label>
                  <Input
                    data-testid="newmatch-opponent"
                    value={opponent}
                    onChange={(e) => setOpponent(e.target.value)}
                    placeholder="Namn på motståndarlag"
                    className="bg-black border-[#3F3F46] text-white focus-visible:ring-[#34C759] rounded-none"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-zinc-400 uppercase tracking-widest text-xs">
                  Turnering / serie (valfritt)
                </Label>
                <Select value={tournamentId} onValueChange={setTournamentId}>
                  <SelectTrigger
                    data-testid="newmatch-tournament"
                    className="bg-black border-[#3F3F46] text-white rounded-none"
                  >
                    <SelectValue placeholder="Ingen koppling" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-[#27272A] text-white">
                    <SelectItem value="__none__">
                      <span className="text-zinc-400">Ingen koppling</span>
                    </SelectItem>
                    {tournaments.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <div className="flex items-center gap-2">
                          <Trophy size={12} className="text-[#FFD60A]" />
                          <span>{t.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </section>

            <div className="flex justify-end">
              <Button
                data-testid="newmatch-submit"
                onClick={submit}
                disabled={!canSubmit || saving}
                className="h-14 px-8 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest disabled:opacity-50"
              >
                <PlayCircle size={18} className="mr-2" />
                {saving ? "Startar…" : "Starta och logga skott"}
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
